/**
 * @fileoverview Color palette for storylines.
 *
 * Provides a fixed palette of 16 visually distinct colors for storyline visualization.
 * Colors are designed to be distinguishable in both light and dark modes.
 */

/**
 * Storyline color definition with background, border, and text colors.
 */
export interface StorylineColor {
  /** Background color for the storyline badge/indicator */
  bg: string;
  /** Border color */
  border: string;
  /** Text color for contrast */
  text: string;
  /** Lighter background for coverage grid cells */
  bgLight: string;
}

/**
 * Palette of 16 visually distinct colors for storylines.
 * Each color has variants for different UI contexts.
 */
export const STORYLINE_COLOR_PALETTE: StorylineColor[] = [
  // Blues
  { bg: 'bg-blue-500', border: 'border-blue-600', text: 'text-white', bgLight: 'bg-blue-100 dark:bg-blue-900/30' },
  { bg: 'bg-sky-500', border: 'border-sky-600', text: 'text-white', bgLight: 'bg-sky-100 dark:bg-sky-900/30' },
  // Greens
  { bg: 'bg-emerald-500', border: 'border-emerald-600', text: 'text-white', bgLight: 'bg-emerald-100 dark:bg-emerald-900/30' },
  { bg: 'bg-teal-500', border: 'border-teal-600', text: 'text-white', bgLight: 'bg-teal-100 dark:bg-teal-900/30' },
  // Warm colors
  { bg: 'bg-amber-500', border: 'border-amber-600', text: 'text-white', bgLight: 'bg-amber-100 dark:bg-amber-900/30' },
  { bg: 'bg-orange-500', border: 'border-orange-600', text: 'text-white', bgLight: 'bg-orange-100 dark:bg-orange-900/30' },
  // Reds/Pinks
  { bg: 'bg-rose-500', border: 'border-rose-600', text: 'text-white', bgLight: 'bg-rose-100 dark:bg-rose-900/30' },
  { bg: 'bg-pink-500', border: 'border-pink-600', text: 'text-white', bgLight: 'bg-pink-100 dark:bg-pink-900/30' },
  // Purples
  { bg: 'bg-violet-500', border: 'border-violet-600', text: 'text-white', bgLight: 'bg-violet-100 dark:bg-violet-900/30' },
  { bg: 'bg-purple-500', border: 'border-purple-600', text: 'text-white', bgLight: 'bg-purple-100 dark:bg-purple-900/30' },
  // Neutrals with color
  { bg: 'bg-indigo-500', border: 'border-indigo-600', text: 'text-white', bgLight: 'bg-indigo-100 dark:bg-indigo-900/30' },
  { bg: 'bg-cyan-500', border: 'border-cyan-600', text: 'text-white', bgLight: 'bg-cyan-100 dark:bg-cyan-900/30' },
  // Additional colors
  { bg: 'bg-lime-500', border: 'border-lime-600', text: 'text-white', bgLight: 'bg-lime-100 dark:bg-lime-900/30' },
  { bg: 'bg-fuchsia-500', border: 'border-fuchsia-600', text: 'text-white', bgLight: 'bg-fuchsia-100 dark:bg-fuchsia-900/30' },
  { bg: 'bg-red-500', border: 'border-red-600', text: 'text-white', bgLight: 'bg-red-100 dark:bg-red-900/30' },
  { bg: 'bg-yellow-500', border: 'border-yellow-600', text: 'text-black', bgLight: 'bg-yellow-100 dark:bg-yellow-900/30' },
];
